<template>
    <div>
        <div class="row mt-3 mb-3 align-items-center">
            <div class="col-auto">
                <div class="green-filter d-flex">
                    <b-form-select 
                        v-model="selectedUnit" 
                        :options="groupingUnits"
                        :disabled="loading"
                        size="sm"
                        class="mr-2"
                        style="max-width: 80px;"
                    ></b-form-select>

                    <div class="input-group">
                        <input type="date" class="form-control start-input-date" v-model="period_start" :disabled="loading">
                        <input type="date" class="form-control finish-input-date" v-model="period_end" :disabled="loading">
                        <div class="input-group-append">
                            <button
                                class="btn btn-outline-secondary"
                                type="button"
                                @click="fetchAnalytics"
                                :disabled="loading"
                            >Обновить</button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col"></div>
            <div class="col-auto">
                <div
                    class="d-flex justify-content-between align-items-center"
                >
                    <b-form-checkbox 
                        v-model="conclusionsChecked"
                        name="conclusions-check-button"
                        switch
                        class="my-0 mr-3"
                    >
                        Итоги
                    </b-form-checkbox>                

                    <template v-if="!conclusionsChecked">
                        <!-- Pagination Component -->
                        <b-pagination
                            v-if="totalRows > perPage"
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            @change="updatePagination"
                            class="my-0 mr-3"
                        ></b-pagination>
                        
                        <!-- Rows Per Page Selector -->
                        <b-form-group 
                            label="" 
                            class="my-0"
                            style="max-width: 80px;"
                        >
                            <b-form-select
                                v-model="perPage"
                                :options="perPageOptions"
                                :disabled="loading"
                                size="sm"
                            ></b-form-select>
                        </b-form-group>
                    </template>
                </div>         
            </div>
        </div>

        <SkeletonBuilder v-if="loading" />

        <div class="mb-4">
            <!-- Summary Table -->
            <div 
                v-if="conclusionsChecked && !loading"
                class="position-relative"
            >
                <b-table
                    id="financeSummaryTable"
                    hover
                    bordered
                    :items="groupedData.concat(groupedDataItogo)"
                    :fields="summaryFields"
                >
                    <!-- Income Column -->
                    <template #cell(income)="data">
                        {{ currencyFormatter(data.value) }}
                    </template>

                    <!-- Expenses Column -->
                    <template #cell(expenses)="data">
                        {{ currencyFormatter(data.value) }}
                    </template>

                    <!-- Default Formatting -->
                    <template #cell()="data">
                        {{ data.value }}
                    </template>                
                </b-table>
            </div>

            <div 
                v-if="conclusionsChecked && !loading" 
                class="position-relative mt-5"
            >
                <p class="text-center font-weight-bold">График доходов и расходов, руб.</p>
                <B2cFinanceIncomeExpancesSummaryChart :grouped-data="groupedData" />
            </div>        

            <div 
                v-if="!conclusionsChecked"
                class="position-relative"
            >
                <div
                    v-if="items.length && items.length > 0"
                    class="position-absolute text-right small text-secondary"
                    style="right: 0px; top: -19px;"
                >Записей: {{ items.length }}{{ items.length > 9999 ? "+":"" }}</div>

                <b-table
                    id="financeTable"
                    striped hover
                    :items="paginatedItems"
                    :fields="fields"
                    :sticky-header="computedWindowHeight"
                    :busy="loading"
                    :sort-by="'order_id'"
                    :sort-desc="false"
                >
                    <template #cell()="data">
                        <template v-if="data.field.loading">
                            <template v-if="!data.value"><i class="fas fa-spinner fa-spin ml-1"></i></template>
                            <template v-else>{{ data.value }}</template>
                        </template>
                        <template v-else>
                            {{ data.value || "—" }}
                        </template>
                    </template>
                    <template #cell(invoice_number)="data">
                        <span class="sidebar-link" @click="openFinanceSidebar(data.item.id)">{{ data.value }}</span>
                    </template>
                    <template #cell(sum)="data">
                        {{ formatNumbers(data.value) }}
                    </template>
                    <template #cell(sum_currency)="data">
                        {{ formatNumbers(data.value) }}
                    </template>
                    <template #cell(sum_difference)="data">
                        {{ formatNumbers(data.value) }}
                    </template>
                    <template #cell(sum_payment)="data">
                        {{ formatNumbers(data.value) }}
                    </template>
                    <template #cell(commission_bank)="data">
                        {{ formatNumbers(data.value) }}
                    </template>
                    <template #cell(debt)="data">
                        {{ formatNumbers(data.value) }}
                    </template>
                    <!-- <template #cell(debt)="data">
                        {{ debtCalc(data.item, data.value) }}
                    </template> -->
                    <template #cell(final_sum_to_buyers_no_nds)="data">
                        {{ formatNumbers(data.value) }}
                    </template>
                    <template #cell(final_sum_to_suppliers_no_nds)="data">
                        {{ formatNumbers(data.value) }}
                    </template>
                    <template #cell(final_sum)="data">
                        {{ formatNumbers(data.value) }}
                    </template>
                    <template #cell(final_sum_no_nds)="data">
                        {{ formatNumbers(data.value) }}
                    </template>      
                    <template #cell(receive_datetime)="data">
                        <template v-if="!isExportData">{{ data.value | formattedDateTime }}</template>
                        <template v-else>{{ data.value }}</template>
                    </template>
                    <template #cell(processing_end_datetime)="data">
                        <template v-if="!isExportData">{{ data.value | formattedDateTime }}</template>
                        <template v-else>{{ data.value }}</template>
                    </template>
                    <template #cell(invoice_datetime)="data">
                        <template v-if="!isExportData">{{ data.value | formattedDate }}</template>
                        <template v-else>{{ data.value }}</template>
                    </template>
                    <template #cell(realization_datetime)="data">
                        <template v-if="!isExportData">{{ data.value | formattedDate }}</template>
                        <template v-else>{{ data.value }}</template>
                    </template>
                    <template #cell(payment_datetime)="data">
                        <template v-if="!isExportData">{{ data.value | formattedDate }}</template>
                        <template v-else>{{ data.value }}</template>
                    </template>
                    <template #cell(close_finance_date)="data">
                        <template v-if="!isExportData">{{ data.value | formattedDate }}</template>
                        <template v-else>{{ data.value }}</template>
                    </template>
                    <template #cell(finance_ids)="data">
                        <span v-for="(item, index) in data.item.finance_ids" :key="item.id" class="sidebar-link" @click="openFinanceSidebar(item.id)">{{ item.invoice_number }}<template v-if="index != data.item.finance_ids.length - 1">, </template></span>
                    </template>
                    <template #cell(services_list)="data">
                        <div class="big-table-cells">
                            <span v-html="formatServicesList(data.item.services_list)"></span>
                        </div>
                    </template>  
                    <template v-slot:table-busy>
                        <div class="text-center text-success my-5">
                            <b-spinner class="align-middle"></b-spinner>
                        </div>
                    </template>
                </b-table>
            </div>        
        </div>
    </div>
</template>

<script>
import { openFinanceSidebar } from "@/mixins/openFinanceSidebar";
import SkeletonBuilder from "@/components/ui/Loaders/SkeletonBuilder";
import B2cFinanceIncomeExpancesSummaryChart from "@/components/finance/analytic/B2cFinanceIncomeExpancesSummaryChart";
import {
    excelExportHelpers
} from "@/mixins";
import { API_FINANCE_ANALYTICS_INCOME_EXPANSES } from "@/constants";

export default {
    name: "FinanceAnalytics",
    components: {
        SkeletonBuilder,
        B2cFinanceIncomeExpancesSummaryChart
    },
    mixins: [
        excelExportHelpers,
        openFinanceSidebar,
    ],
    data() {
        return {
            loading: false,
            computedWindowHeight: '',
            period_start: '',
            period_end: '',
            conclusionsChecked: true,
            selectedUnit: 'B2C',
            groupingUnits: [
                { value: 'B2C', text: 'B2C' },
                { value: 'B2B', text: 'B2B' },
            ],
            fields: [
                {
                    'key': 'id',
                    'label': 'ID',
                    'stickyColumn': true,
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'invoice_number',
                    'label': 'Номер счета',
                    'stickyColumn': true,
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'invoice_datetime',
                    'label': 'Дата счета',
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'service',
                    'label': 'Услуга',
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'service_for_partner', // Надо разобраться
                    'label': 'Тип услуги контрагентов',
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'services_list',
                    'sortBy': 'services_list.properties',
                    'label': 'Перечень услуг',
                    'sortable': false,
                    'sortDirection': 'desc',
                    'visible': true,
                },
                {
                    'key': 'external_company',
                    'sortBy': 'external_company',
                    'label': 'Закреплён в компании',
                    'stickyColumn': true,
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'payer',
                    'sortBy': 'payer',
                    'label': 'Плательщик',
                    'stickyColumn': true,
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'company',
                    'sortBy': 'company',
                    'label': 'Получатель',
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'invoice_type_name',
                    'label': 'Тип счета',
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'realization_datetime',
                    'label': 'Дата реализации',
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'currency',
                    'label': 'Валюта',
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'sum',
                    'label': 'Сумма',
                    'stickyColumn': false,
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true,
                    'tdAttr': {
                        'data-t': 'n'
                    }
                },
                {
                    'key': 'sum_currency',
                    'label': 'Сумма в валюте',
                    'stickyColumn': false,
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true,
                    'tdAttr': {
                        'data-t': 'n'
                    }
                },
                {
                    'key': 'zero_rate',
                    'label': 'Нулевая ставка?',
                    'stickyColumn': false,
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'natural',
                    'label': 'Натуральная форма?',
                    'stickyColumn': false,
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'with_nds',
                    'label': 'с НДС',
                    'stickyColumn': false,
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
                {
                    'key': 'commission_bank',
                    'label': 'Комиссия банка',
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true,
                    'tdAttr': {
                        'data-t': 'n'
                    }
                },
                {
                    'key': 'sum_difference',
                    'label': 'Суммовая разница',
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true,
                    'tdAttr': {
                        'data-t': 'n'
                    }
                },
                {
                    'key': 'sum_payment',
                    'label': 'Оплачено',
                    'stickyColumn': false,
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true,
                    'tdAttr': {
                        'data-t': 'n'
                    }
                },
                {
                    'key': 'debt',
                    'label': 'Задолженность, рубли',
                    'stickyColumn': false,
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true,
                    'tdAttr': {
                        'data-t': 'n'
                    }
                },
                {
                    'key': 'payment_datetime',
                    'label': 'Дата финальной оплаты',
                    'sortable': true,
                    'sortDirection': 'desc',
                    'visible': true
                },
            ],
            summaryFields: [
                { key: 'external_company', label: 'Клиент', thClass: 'table-gray' },
                { key: 'income', label: 'ДОХОДЫ', thClass: 'table-bright-success' },
                { key: 'expenses', label: 'РАСХОДЫ', thClass: 'table-bright-warning' },
                { key: 'difference', label: 'Разница', thClass: 'table-gray', formatter: 'currencyFormatter' },
            ],
            currentPage: 1,
            perPage: 50,
            totalRows: 0,
            items: [],
            paginatedItems: [],
            groupedData: [],
            groupedDataItogo: [],
            perPageOptions: [
                { value: 5, text: '5' },
                { value: 10, text: '10' },
                { value: 25, text: '25' },
                { value: 50, text: '50' },
                { value: 100, text: '100' },
            ],
        };
    },
    watch: {
        currentPage() {
            this.updatePagination();
        },
        perPage() {
            this.updatePagination();
        },
        selectedUnit() {
            this.fetchAnalytics();
        },
    },    
    methods: {
        handlerHeightWindow() {
            let deleteHeight = 165
            this.computedWindowHeight = `${window.top.innerHeight - deleteHeight}px`
        },
        convertToISODate(dateStr) {
            const [day, month, year] = dateStr.split(/[.\/\s]/); // Split by dot or other delimiters
            return `${year}-${month}-${day}`;
        },
        currencyFormatter(value) {
            return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(value);
        },
        fetchAnalytics() {
            this.loading = true;
            const request = {
                period_start: this.period_start,
                period_end: this.period_end,
                unit: this.selectedUnit
            };

            axios.post(API_FINANCE_ANALYTICS_INCOME_EXPANSES, request)
                .then(({ data }) => {
                    this.items = data.data;
                    this.groupedData = data.groupedData;
                    this.groupedDataItogo = data.groupedDataItogo;
                    this.totalRows = this.items.length;
                    this.updatePagination();

                    // Convert the returned dates to "yyyy-MM-dd" format
                    this.period_start = this.convertToISODate(data.params.period_start);
                    this.period_end = this.convertToISODate(data.params.period_end);
                })
                .catch(error => {
                    console.error('Ошибка при загрузке данных:', error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        updatePagination() {
            // Calculate the items for the current page
            const start = (this.currentPage - 1) * this.perPage;
            const end = this.currentPage * this.perPage;
            this.paginatedItems = this.items.slice(start, end);
        },        
        formatServicesList(services_list) {
            if (services_list.data && services_list.data.length > 0) {
                let finalText = '<ul class="service-list-in-main-table">';

                services_list.data.forEach(service => {
                    finalText += `<li>${service.name}</li>`;
                });

                finalText += '</ul>';

                return finalText;
            }

            return '—';
        }
    },
    created() {
        window.addEventListener("resize", this.handlerHeightWindow);
    },
    destroyed() {
        window.removeEventListener("resize", this.handlerHeightWindow);
    },
    mounted() {
        this.handlerHeightWindow();
        this.fetchAnalytics();
    },
};
</script>

<style scoped>
.green-filter {
    align-items: center;
}
</style>